<template>
  <div>
    <!-- CNC Ads -->
    <DefaultAdsCncWrapper
      v-if="cncAdsPage"
      :position="UniFeAdsPosition.ABOVE_HEADER"
      :page="cncAdsPage"
    />

    <main class="branding-wrapper main min-h-screen lg:min-h-80">
      <section class="section">
        <!-- Main gallery / image -->
        <DefaultArticlesGalleryFull
          v-if="getMainGalleryImages().length > 0"
          :images="getMainGalleryImages()"
          :detail-url="getDetailPath()"
          :title="articleStore.article?.title || undefined"
          :ad-page="cncAdsPage"
        />
      </section>
    </main>

  </div>
</template>

<script setup lang="ts">
import {ARTICLE_QUERY_TYPE, useArticlesStore} from "~/stores/useArticlesStore"
import {ArticleStoreType} from "~/libs/constants/ArticleStoreType"
import {useHtmlToPlainText} from "~/composables/useHtmlToPlainText"
import {CncAdsPage} from "~/libs/constants/CncAdsPage"
import {DEBUG_COOKIE} from "~/libs/constants/constants"
import {decode} from 'html-entities'
import {useSeoWrapper} from "~/composables/useSeoWrapper"
import {useArticleTools} from "~/composables/useArticleTools"
import {UniFeAdsPosition} from "~/libs/constants/UniFeAdsPosition"
import type AssetLocal from "~/libs/interfaces/AssetLocal"

// i18n routing
definePageMeta({
  name: 'galleries-articleUrl',
  middleware: [
    function (to, from) {
      const localePath = useLocalePath()
      let newPath = decodeURIComponent(to.params.articleUrl as string)

      if (newPath?.at(0) === '/') {
        newPath = newPath.slice(1, newPath.length)
      }
      if (newPath?.at(newPath?.length-1) === '/') {
        newPath = newPath.slice(0, newPath.length-1)
      }

      const localePathUrl = decodeURIComponent(localePath({name: 'galleries-articleUrl', params: {articleUrl: encodeURIComponent(newPath)}}))
      if (to.fullPath !== localePathUrl) {
        const debugCookie = useCookie(DEBUG_COOKIE)
        if (debugCookie) {
          const logger = useFrontendLogger()
          logger?.info('Page moved - 301', {
            originalPath: to.fullPath,
            newPath: localePathUrl
          })
        }
        return navigateTo(localePathUrl, { redirectCode: 301 })
      }
    },
  ],
})
false && /*#__PURE__*/ defineI18nRoute({
  paths: {
    cs: '/galerie/[articleUrl]',
    en: '/gallery/[articleUrl]'
  }
})

const config = useRuntimeConfig()
const route = useRoute()
const logger = useFrontendLogger()
const cncAdsPage = CncAdsPage.ARTICLE_GALLERY

const debugCookie = useCookie(DEBUG_COOKIE)

const articleStore = useArticlesStore(ArticleStoreType.ARTICLE_GALLERY)

const mainImage = computed(() => {
  return articleStore.article?.previewAsset?.urlWithProtocol || articleStore.article?.articleLinkAssetList?.edges?.at(0)?.node?.asset?.urlWithProtocol || null
})

watch(
  () => {
    return articleStore.article?.id
  }, async (newValue) => {
    if (newValue) {
      setSeoMeta()
    }
  }
)

const seoWrapper = useSeoWrapper()

// Set Seo meta data
const setSeoMeta = () => {
  // Set SEO meta
  seoWrapper.setSeoMeta({
    title: decode(articleStore.article?.title),
    ...(articleStore.article?.perex && {
      description: useHtmlToPlainText(articleStore.article?.perex),
    }),
    ogImage: () => mainImage.value,
  })
}

const getAllArticleUrls = () => {
  const path = decodeURIComponent(route.params.articleUrl as string)
  return [
    `${path}`,
    `/${path}`,
    `/${path}/`,
    `${path}/`,
  ]
}

articleStore.setLimit(1)
const filters = {
  paths: getAllArticleUrls()
}

const getDetailPath = () => {
  return articleStore.article ? useArticleTools(articleStore.article).getDetailPath() : undefined
}

try {
  if (import.meta.server) {
    await articleStore.fetchArticles(filters, ARTICLE_QUERY_TYPE.DETAIL, {
      forceRefetch: true,
      useDefaultFilters: false,
      usePublishedStateFilters: false,
    })
    setSeoMeta()
  }
} catch (e) {
  throw createError({
    statusCode: 404,
    statusMessage: 'Page Not Found'
  })
}

const getMainGalleryImages = (): AssetLocal[] => {
  const pathRegex = /^(http|https)/;
  const images: AssetLocal[] = []
  if (articleStore.article?.articleLinkAssetList?.edges) {
    articleStore.article?.articleLinkAssetList?.edges.forEach((image) => {
      if (image?.node?.asset?.urlWithProtocol && pathRegex.test(image?.node?.asset?.urlWithProtocol)) {
        images.push({
          urlWithProtocol: image.node?.asset?.urlWithProtocol,
          caption: image.node?.asset?.caption,
          title: image.node?.asset?.title,
          source: image.node?.asset?.source,
        })
      } else {
        logger.info('Article asset path is invalid', {
          ArticleId: `${articleStore.article?.id}`,
          ArticlePath: `${articleStore.article?.path}`,
          AssetId: image?.node?.id,
          AssetPath: image?.node?.asset?.urlWithProtocol,
        })
      }
    })
    // remove the first image when the first image is the same as the header photo
    if (mainImage.value && images.length > 0 && mainImage.value === images.at(0)?.urlWithProtocol) {
      images.shift()
    }
  }
  return images
}

onMounted(async () => {
  // load only when path changed
  if (!articleStore.article?.path || !filters.paths.includes(articleStore.article?.path)) {
    try {
      await articleStore.fetchArticles(filters, ARTICLE_QUERY_TYPE.DETAIL, {
        forceRefetch: true,
        useDefaultFilters: false,
        usePublishedStateFilters: false,
      })
      setSeoMeta()
    } catch (e) {
      // show error message?
      throw createError({
        statusCode: 404,
        statusMessage: 'Page Not Found'
      })
    }
  }

  if (debugCookie) {
    logger?.info('Loaded Article', articleStore.article)
  }

  /**
   * CncAds Page change
   */
  useCncAdsWrapper().changePageAds(cncAdsPage, {
    route: route
  })
})

</script>
